/**
 * Identify — manufacturer, serial, model number, and whether the device
 * arrived sealed.
 *
 * Fields adapt to the device-type schema: free text with suggestions for
 * laptops / phones / tablets, a fixed list for the rest. "Brand new, in
 * unopened box" lives here rather than under Damage (where the wizard had it),
 * because it is a fact about the device and every type can arrive sealed —
 * on the Damage step, a desktop (no damage rows) could never record it.
 */

import React from 'react';
import { Autocomplete, FormControlLabel, MenuItem, Stack, Switch, TextField } from '@mui/material';

import {
  isSerialOptional,
  modelNumberHint,
  showsEmcNumber,
  type IdentifyValidation,
  type InspectionDraftState,
  type InspectionSchemaEntry,
} from '../../../../utils/inspectionSchema';

/** Shown under a field the AI filled from a label photo, until someone edits it. */
const FROM_PHOTO = 'Read from photo — check it matches the label';

export interface IdentifySectionProps {
  schema: InspectionSchemaEntry;
  draft: InspectionDraftState;
  /** Shown only once someone has tried to complete — not while typing. */
  errors?: IdentifyValidation;
  readOnly?: boolean;
  onChange: (patch: Partial<InspectionDraftState>) => void;
}

export const IdentifySection: React.FC<IdentifySectionProps> = ({
  schema,
  draft,
  errors,
  readOnly = false,
  onChange,
}) => {
  const serialOptional = isSerialOptional(schema, draft);
  const serialLabel = schema.deviceType === 'phone' ? 'IMEI / Serial number' : 'Serial number';

  return (
    <Stack spacing={2}>
      {schema.manufacturerKind === 'autocomplete' ? (
        <Autocomplete
          freeSolo
          autoHighlight
          disabled={readOnly}
          options={schema.manufacturerOptions}
          value={draft.manufacturer ?? ''}
          onChange={(_e, v) => onChange({ manufacturer: typeof v === 'string' ? v : '' })}
          onInputChange={(_e, v, reason) => {
            // 'reset' is MUI echoing a value set programmatically (an AI
            // pre-fill, or a pick handled by onChange) — not an edit.
            if (reason !== 'reset') onChange({ manufacturer: v });
          }}
          renderInput={params => (
            <TextField
              {...params}
              label="Manufacturer"
              required
              error={!!errors?.manufacturer}
              helperText={errors?.manufacturer ?? (draft.aiFilled.manufacturer ? FROM_PHOTO : undefined)}
              autoComplete="off"
            />
          )}
        />
      ) : (
        <TextField
          select
          label="Manufacturer"
          required
          disabled={readOnly}
          value={draft.manufacturer ?? ''}
          onChange={e => onChange({ manufacturer: e.target.value })}
          error={!!errors?.manufacturer}
          helperText={errors?.manufacturer ?? (draft.aiFilled.manufacturer ? FROM_PHOTO : undefined)}
        >
          {schema.manufacturerOptions.map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        label={`${serialLabel}${serialOptional ? ' (optional)' : ''}`}
        required={!serialOptional}
        disabled={readOnly}
        value={draft.serialNumber ?? ''}
        onChange={e => onChange({ serialNumber: e.target.value })}
        error={!!errors?.serialNumber}
        helperText={errors?.serialNumber ?? (draft.aiFilled.serialNumber ? FROM_PHOTO : undefined)}
        autoComplete="off"
      />

      {/* Optional, never blocks completion: it identifies the exact model
          (Apple A-number, Surface "Model 1866", Lenovo MTM, HP P/N). */}
      <TextField
        label="Model number (optional)"
        disabled={readOnly}
        value={draft.modelNumber ?? ''}
        onChange={e => onChange({ modelNumber: e.target.value })}
        helperText={draft.aiFilled.modelNumber ? FROM_PHOTO : modelNumberHint(draft.manufacturer, schema.deviceType)}
        inputProps={{ maxLength: 100 }}
        autoComplete="off"
      />

      {showsEmcNumber(schema.deviceType, draft.manufacturer) && (
        <TextField
          label="EMC number (optional)"
          disabled={readOnly}
          value={draft.emcNumber ?? ''}
          onChange={e => onChange({ emcNumber: e.target.value })}
          helperText={draft.aiFilled.emcNumber ? FROM_PHOTO : 'Printed beside the A-number (e.g. EMC 3598)'}
          inputProps={{ maxLength: 20 }}
          autoComplete="off"
        />
      )}

      {/* "No visible serial" is chosen on the Photos section's serial tile —
          one control, beside the photo it stands in for. */}
      {draft.serialNotLegible && (
        <div className="ci-caption">No visible serial — chosen in Photos, so the serial is optional.</div>
      )}

      {schema.serialOptional && draft.manufacturer === 'Custom' && (
        <div className="ci-caption">
          Custom-built desktops often don&apos;t have a manufacturer serial. Leave blank if so.
        </div>
      )}

      <div className={`ci-rowcard${draft.brandNewInBox ? ' ci-rowcard--on' : ''}`}>
        <FormControlLabel
          control={
            <Switch
              checked={draft.brandNewInBox}
              disabled={readOnly}
              onChange={e => onChange({ brandNewInBox: e.target.checked })}
            />
          }
          label={
            <div>
              <div className="ci-strong">Brand new, in unopened box</div>
              <div className="ci-caption">
                Skips the damage and item checks. Photos then asks only for the side of the box with the serial label.
              </div>
            </div>
          }
          sx={{ alignItems: 'flex-start', mr: 0 }}
        />
      </div>
    </Stack>
  );
};
